// frontend/src/pages/Dashboard.jsx - COMPLETE FULLY FUNCTIONAL (Confidential Supplier)
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import {
  getDashboardStats,
  getRecentOrders,
  getLowStockProducts,
  getAllOrders,
  getAllShipments,
  getProducts,
  deleteProduct,
  updateOrderStatus,
  updateShipmentStatus,
  getUsers,
  getAnalytics,
  toggleUserStatus,
  getAdminUserStats,
  getUserOrders,
  getMyUserStats,
  updateProfile,
  changePassword,
  getPaymentMethods,
  addPaymentMethod,
  removePaymentMethod,
  getPayoutHistory,
  requestPayout,
  getAllPreOrders,
  getPreOrderStats,
  updatePreOrderStatus,
  getAvailableSizes,
  getPreOrderSizes,
} from "../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faShoppingCart,
  faUsers,
  faTruck,
  faEdit,
  faTrash,
  faChartLine,
  faExchangeAlt,
  faUserPlus,
  faToggleOn,
  faToggleOff,
  faUserTie,
  faTractor,
  faCreditCard,
  faDollarSign,
  faUserCircle,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faSave,
  faKey,
  faPlus,
  faTimes,
  faCheckCircle,
  faExclamationTriangle,
  faSearch,
  faClock,
  faCrown,
  faShieldAlt,
  faStore,
  faSpinner,
  faClipboardList,
  faCalendarCheck,
  faMoneyBillWave,
  faGlobe,
  faWeightScale,
  faBoxes,
  faShip,
  faRobot,
  faArrowRight,
  faBuilding,
  faHandshake,
  faStar,
  faLightbulb,
  faBriefcase,
  faUserCog,
  faCode,
  faCheck,
  faEye,
  faChartPie,
  faTag,
  faCalendarAlt,
  faLocationDot,
  faUserCheck,
  faMobileAlt,
  faMoneyBillTransfer,
  faCity,
  faAddressCard,
  faClipboardCheck,
  faBoxOpen,
  faWarehouse,
  faTachometerAlt,
  faImage,
  faQuoteLeft,
  faComment,
  faReply,
  faRocket,
  faHourglassHalf,
  faCheckDouble,
  faTimesCircle,
  faWallet,
  faRepeat,
  faStarHalfAlt,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import Loader from "../components/UI/Loader";
import ProductForm from "../components/Dashboard/ProductForm";
import ShipmentForm from "../components/Dashboard/ShipmentForm";
import AnalyticsCards from "../components/Dashboard/AnalyticsCards";
import SalesChart from "../components/Dashboard/SalesChart";
import PaymentMethods from "../components/Dashboard/PaymentMethods";
import PayoutComponent from "../components/Dashboard/PayoutComponent";
import TrafficSources from "../components/Dashboard/TrafficSources";
import TopProducts from "../components/Dashboard/TopProducts";
import TeamMemberForm from "../components/Dashboard/TeamMemberForm";
import TestimonialManager from "../components/Dashboard/TestimonialManager";
import OrderDetailsModal from "../components/Dashboard/OrderDetailsModal";
import ShipmentDetailsModal from "../components/Dashboard/ShipmentDetailsModal";
import PreOrderManager from "../components/Dashboard/PreOrderManager";
import TeamManagement from "../components/Dashboard/TeamManagement";
import AIStatsCard from "../components/Dashboard/AIStatsCard";
import StatsGrid, { StatCard } from "../components/Dashboard/StatsGrid";
import DashboardTabs from "../components/Dashboard/DashboardTabs";
import DataTable from "../components/Dashboard/DataTable";
import SupplierStats from "../components/Dashboard/SupplierStats";
import "../styles/dashboard.scss";

// ============================================
// CONFIDENTIAL SUPPLIER INFORMATION
// ============================================
// Supplier details are kept confidential.
// All products are displayed as "Globally Sourced"
const SOURCE_LABEL = "Globally Sourced";
const SOURCE_ICON = faGlobe;

// ============================================
// HELPER FUNCTIONS
// ============================================
const getProductImage = (product) => {
  if (product?.images?.[0]) return product.images[0];
  if (product?.thumbnail) return product.thumbnail;
  if (product?.image) return product.image;
  return "/placeholder.png";
};

const getStatusColor = (status) => {
  const colors = {
    pending: "#D9A321",
    processing: "#3b82f6",
    confirmed: "#8b5cf6",
    shipped: "#8b5cf6",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    cancelled: "#dc2626",
    refunded: "#6b7280",
    ordered: "#D9A321",
    in_transit: "#f59e0b",
    customs: "#ef4444",
    delayed: "#dc2626",
    completed: "#16a34a",
    failed: "#dc2626",
    paid: "#16a34a",
    unpaid: "#dc2626",
    processing_payment: "#f59e0b",
    failed_payment: "#dc2626",
    refunded_payment: "#6b7280",
    deposit_received: "#8b5cf6",
    ready_for_pickup: "#10b981",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusLabel = (status) => {
  const labels = {
    pending: "Pending",
    processing: "Processing",
    confirmed: "Confirmed",
    shipped: "Shipped",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    cancelled: "Cancelled",
    refunded: "Refunded",
    ordered: "Order Received",
    in_transit: "In Transit",
    customs: "Customs Clearance",
    delayed: "Delayed",
    completed: "Completed",
    failed: "Failed",
    paid: "Paid",
    unpaid: "Unpaid",
    processing_payment: "Processing Payment",
    failed_payment: "Payment Failed",
    refunded_payment: "Refunded",
    deposit_received: "Deposit Received",
    ready_for_pickup: "Ready for Pickup",
  };
  return labels[status] || status;
};

const getStatusIcon = (status) => {
  const icons = {
    pending: faClock,
    processing: faSpinner,
    confirmed: faCheckCircle,
    shipped: faRocket,
    out_for_delivery: faTruck,
    delivered: faCheckDouble,
    cancelled: faTimesCircle,
    refunded: faWallet,
    ordered: faClipboardCheck,
    in_transit: faShip,
    customs: faShieldAlt,
    delayed: faExclamationTriangle,
    completed: faCheckCircle,
    failed: faExclamationTriangle,
    paid: faMoneyBillWave,
    unpaid: faExclamationTriangle,
    processing_payment: faSpinner,
    failed_payment: faExclamationTriangle,
    refunded_payment: faMoneyBillWave,
    deposit_received: faMoneyBillWave,
    ready_for_pickup: faBox,
  };
  return icons[status] || faClock;
};

const getUserAvatar = (user) => {
  if (!user) return null;
  const avatarSource =
    user?.profileImage || user?.avatar || user?.image || null;
  if (
    avatarSource &&
    (avatarSource.startsWith("data:") || avatarSource.startsWith("http"))
  ) {
    return avatarSource;
  }
  return null;
};

const safeArray = (data) => (Array.isArray(data) ? data : []);

// ============================================
// MAIN DASHBOARD COMPONENT
// ============================================
const Dashboard = () => {
  const {
    user,
    refreshUser,
    logout,
    isAdmin,
    isSuperAdmin,
    isTeamMember,
    isFarmer,
    isSupplier,
    hasPermission,
    getEffectiveRole,
    getEffectiveRoleLabel,
    getRoleIcon,
    getRoleColor,
    getDashboardTabs,
    getDashboardConfig,
    isPendingApproval,
  } = useAuth();
  const { formatAmount, currency, toggleCurrency } = useCurrency();

  // Get role-based configuration
  const role = getEffectiveRole();
  const config = getDashboardConfig();
  const isTeamRole =
    isTeamMember &&
    role !== "customer" &&
    role !== "farmer" &&
    role !== "supplier";

  // State
  const [stats, setStats] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [allShipments, setAllShipments] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [preOrders, setPreOrders] = useState([]);
  const [preOrderStats, setPreOrderStats] = useState({});
  const [availableSizes, setAvailableSizes] = useState([]);
  const [preOrderSizes, setPreOrderSizes] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalVisitors: 0,
    conversionRate: 0,
    avgOrderValue: 0,
    cartAbandonment: 0,
    clv: 0,
    repeatRate: 0,
    salesData: [],
    topProducts: [],
    trafficSources: [],
    preOrders: { total: 0, revenue: 0, deposits: 0, totalPacks: 0, totalKg: 0 },
  });
  const [myOrders, setMyOrders] = useState([]);
  const [myStats, setMyStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showShipmentForm, setShowShipmentForm] = useState(false);
  const [showTeamMemberForm, setShowTeamMemberForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [shipmentUpdateData, setShipmentUpdateData] = useState({
    status: "",
    location: "",
    description: "",
  });
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: { street: "", city: "", district: "", country: "Sierra Leone" },
    profileImage: user?.profileImage || null,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [profileMessage, setProfileMessage] = useState({ type: "", text: "" });
  const [updating, setUpdating] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  // Payment State
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    type: "card",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    nameOnCard: "",
    isDefault: false,
  });
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutRequest, setPayoutRequest] = useState({
    amount: "",
    method: "bank_transfer",
    accountDetails: "",
  });

  const [originStats, setOriginStats] = useState({});
  const [shipmentStats, setShipmentStats] = useState({});

  // Permission checks
  const canViewAll = hasPermission("canViewAll");
  const canManageUsers = hasPermission("canManageUsers");
  const canManageProducts = hasPermission("canManageProducts");
  const canManageOrders = hasPermission("canManageOrders");
  const canManageShipments = hasPermission("canManageShipments");
  const canManageTeam = hasPermission("canManageTeam");
  const canManageAnalytics = hasPermission("canManageAnalytics");
  const canManagePayments = hasPermission("canManagePayments");
  const canManagePreOrders = hasPermission("canManagePreOrders");
  const canManageTestimonials = hasPermission("canManageTestimonials");
  const canViewRevenue = hasPermission("canViewRevenue");
  const canApproveTeam = hasPermission("canApproveTeam");

  const isAdminUser = isAdmin();
  const isSuperAdminUser = isSuperAdmin();
  const isTeamMemberUser = isTeamMember();
  const isFarmerUser = isFarmer();
  const isSupplierUser = isSupplier();
  const hasAdminAccess = isAdminUser || isSuperAdminUser || canViewAll;
  const isPending = isPendingApproval();

  // Get user avatar
  const userAvatar = getUserAvatar(user);

  // ============================================
  // GET ROLE HEADER
  // ============================================
  const getRoleHeaderIcon = () => {
    if (isSuperAdminUser) return faCrown;
    if (isAdminUser) return faShieldAlt;
    if (isTeamMemberUser) return faUserTie;
    if (isFarmerUser) return faTractor;
    if (isSupplierUser) return faStore;
    return faUserCircle;
  };

  const getDashboardTitle = () => {
    if (isSuperAdminUser) return "Super Admin Dashboard";
    if (isAdminUser) return "Admin Dashboard";
    if (isTeamMemberUser) {
      const roleLabel = getEffectiveRoleLabel();
      return `${roleLabel} Dashboard`;
    }
    if (isFarmerUser) return "Farmer Dashboard";
    if (isSupplierUser) return "Supplier Dashboard";
    return "My Account";
  };

  // ============================================
  // DATA FETCHING - WITH BETTER ERROR HANDLING
  // ============================================
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const userOrdersPromise = getUserOrders();
      let promises = [userOrdersPromise];

      // Only fetch admin data if user has admin access
      if (hasAdminAccess) {
        promises.push(
          getDashboardStats(),
          getRecentOrders(),
          getLowStockProducts(),
          getAllOrders({ limit: 50 }),
          getAllShipments({ limit: 50 }),
          getProducts({ limit: 100 }),
          getUsers(),
          getAnalytics("30d"),
          getAdminUserStats(),
          getAllPreOrders({ limit: 50 }),
          getPreOrderStats(),
          getAvailableSizes(),
          getPreOrderSizes(),
        );
      } else {
        promises.push(getMyUserStats());
      }

      const results = await Promise.allSettled(promises);

      const getResult = (index, fallback = {}) => {
        const result = results[index];
        return result?.status === "fulfilled"
          ? result.value
          : { data: fallback };
      };

      setMyOrders(safeArray(getResult(0).data));

      if (hasAdminAccess) {
        setStats(getResult(1).data || {});
        setRecentOrders(safeArray(getResult(2).data));
        setLowStockProducts(safeArray(getResult(3).data));
        setAllOrders(safeArray(getResult(4).data?.orders || getResult(4).data));
        setAllShipments(
          safeArray(getResult(5).data?.shipments || getResult(5).data),
        );
        setAllProducts(
          safeArray(getResult(6).data?.products || getResult(6).data),
        );
        setUsers(safeArray(getResult(7).data?.users || getResult(7).data));

        const analyticsData = getResult(8).data || {};
        setAnalytics({
          ...analyticsData,
          preOrders: analyticsData.preOrders || {
            total: 0,
            revenue: 0,
            deposits: 0,
            totalPacks: 0,
            totalKg: 0,
          },
        });
        setUserStats(getResult(9).data || {});
        setPreOrders(safeArray(getResult(10).data?.data || getResult(10).data));
        setPreOrderStats(getResult(11).data?.data || getResult(11).data || {});
        setAvailableSizes(getResult(12).data?.sizes || []);
        setPreOrderSizes(getResult(13).data?.sizes || []);

        const productsData = getResult(6).data?.products || [];
        const origins = {};
        productsData.forEach((p) => {
          // Use "Globally Sourced" instead of specific origin
          origins[SOURCE_LABEL] = (origins[SOURCE_LABEL] || 0) + 1;
        });
        setOriginStats(origins);

        const shipments =
          getResult(5).data?.shipments || getResult(5).data || [];
        const shipmentStatsData = {
          total: shipments.length,
          fromGlobal: shipments.length, // All shipments are globally sourced
          bulk: shipments.filter((s) => s.orderType === "bulk_farmer").length,
          inventory: shipments.filter((s) => s.orderType === "inventory")
            .length,
        };
        setShipmentStats(shipmentStatsData);
      } else {
        setMyStats(getResult(1).data || {});
        if (user) {
          setProfileData({
            name: user.name || "",
            email: user.email || "",
            phone: user.phone || "",
            address: user.address || {
              street: "",
              city: "",
              district: "",
              country: "Sierra Leone",
            },
            profileImage: user.profileImage || null,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      // Don't set error for team members - they just don't have access to admin data
      if (!isTeamMemberUser) {
        setNotification({
          type: "error",
          message: "Failed to load some dashboard data",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [
    hasAdminAccess,
    user,
    isTeamMemberUser,
  ]);

  const fetchPaymentData = useCallback(async () => {
    try {
      const [methods, history] = await Promise.all([
        getPaymentMethods(),
        getPayoutHistory(),
      ]);
      setPaymentMethods(safeArray(methods?.data));
      setPayoutHistory(safeArray(history?.data));
    } catch (error) {
      console.error("Error fetching payment data:", error);
      setPaymentMethods([]);
      setPayoutHistory([]);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
    fetchPaymentData();
  }, [fetchAllData, fetchPaymentData]);

  // ============================================
  // NOTIFICATIONS
  // ============================================
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: "", message: "" }), 5000);
  };

  // ============================================
  // PROFILE HANDLERS
  // ============================================
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setProfileMessage({ type: "", text: "" });
    try {
      const response = await updateProfile(profileData);
      if (response.data.success) {
        setProfileMessage({
          type: "success",
          text: "Profile updated successfully!",
        });
        await refreshUser();
        showNotification("success", "Profile updated successfully!");
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to update profile";
      setProfileMessage({ type: "error", text: msg });
      showNotification("error", msg);
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setProfileMessage({ type: "error", text: "New passwords do not match" });
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setProfileMessage({
        type: "error",
        text: "Password must be at least 6 characters",
      });
      return;
    }
    setUpdating(true);
    try {
      await changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
      );
      setProfileMessage({
        type: "success",
        text: "Password changed successfully!",
      });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      showNotification("success", "Password changed successfully!");
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to change password";
      setProfileMessage({ type: "error", text: msg });
      showNotification("error", msg);
    } finally {
      setUpdating(false);
    }
  };

  // ============================================
  // PAYMENT HANDLERS
  // ============================================
  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    try {
      await addPaymentMethod(newPaymentMethod);
      setShowAddPaymentModal(false);
      fetchPaymentData();
      setNewPaymentMethod({
        type: "card",
        cardNumber: "",
        expiryDate: "",
        cvv: "",
        nameOnCard: "",
        isDefault: false,
      });
      showNotification("success", "Payment method added successfully!");
    } catch (error) {
      const msg =
        error.response?.data?.message || "Failed to add payment method";
      showNotification("error", msg);
    }
  };

  const handleRemovePaymentMethod = async (methodId) => {
    if (
      window.confirm("Are you sure you want to remove this payment method?")
    ) {
      try {
        await removePaymentMethod(methodId);
        fetchPaymentData();
        showNotification("success", "Payment method removed successfully!");
      } catch (error) {
        const msg =
          error.response?.data?.message || "Failed to remove payment method";
        showNotification("error", msg);
      }
    }
  };

  const handleRequestPayout = async (e) => {
    e.preventDefault();
    try {
      await requestPayout(payoutRequest);
      setShowPayoutModal(false);
      fetchPaymentData();
      setPayoutRequest({
        amount: "",
        method: "bank_transfer",
        accountDetails: "",
      });
      showNotification("success", "Payout requested successfully!");
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to request payout";
      showNotification("error", msg);
    }
  };

  // ============================================
  // ADMIN CRUD HANDLERS - With permission checks
  // ============================================
  const handleUpdateOrderStatus = async (orderId, status) => {
    if (!canManageOrders && !hasAdminAccess) {
      showNotification("error", "You don't have permission to update orders");
      return;
    }
    try {
      await updateOrderStatus(orderId, status);
      await fetchAllData();
      showNotification(
        "success",
        `Order status updated to ${getStatusLabel(status)}`,
      );
    } catch (error) {
      console.error("Error updating order status:", error);
      showNotification("error", "Failed to update order status");
    }
  };

  const handleUpdateShipmentStatus = async (shipmentId) => {
    if (!canManageShipments && !hasAdminAccess) {
      showNotification("error", "You don't have permission to update shipments");
      return;
    }
    try {
      await updateShipmentStatus(
        shipmentId,
        shipmentUpdateData.status,
        shipmentUpdateData.location,
        shipmentUpdateData.description,
      );
      setSelectedShipment(null);
      setShipmentUpdateData({ status: "", location: "", description: "" });
      await fetchAllData();
      showNotification("success", "Shipment status updated successfully!");
    } catch (error) {
      console.error("Error updating shipment status:", error);
      showNotification("error", "Failed to update shipment status");
    }
  };

  const handleUpdatePreOrderStatus = async (preOrderId, data) => {
    if (!canManagePreOrders && !hasAdminAccess) {
      showNotification("error", "You don't have permission to update pre-orders");
      return;
    }
    try {
      await updatePreOrderStatus(preOrderId, data);
      await fetchAllData();
      showNotification(
        "success",
        `Pre-order status updated to ${getStatusLabel(data.status)}`,
      );
    } catch (error) {
      console.error("Error updating pre-order status:", error);
      showNotification("error", "Failed to update pre-order status");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!canManageProducts && !hasAdminAccess) {
      showNotification("error", "You don't have permission to delete products");
      return;
    }
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(productId);
        await fetchAllData();
        showNotification("success", "Product deleted successfully!");
      } catch (error) {
        console.error("Error deleting product:", error);
        showNotification("error", "Failed to delete product");
      }
    }
  };

  const handleToggleUserStatus = async (userId) => {
    if (!canManageUsers && !hasAdminAccess) {
      showNotification("error", "You don't have permission to manage users");
      return;
    }
    if (userId === user?._id) {
      showNotification("error", "You cannot change your own account status");
      return;
    }
    try {
      await toggleUserStatus(userId);
      await fetchAllData();
      showNotification("success", "User status updated successfully!");
    } catch (error) {
      console.error("Error toggling user status:", error);
      const msg =
        error.response?.data?.message || "Failed to update user status";
      showNotification("error", msg);
    }
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderOrderStatusBadge = (status) => (
    <span
      className="status-badge"
      style={{ background: getStatusColor(status) }}
    >
      <FontAwesomeIcon icon={getStatusIcon(status)} /> {getStatusLabel(status)}
    </span>
  );

  const renderTableActions = (row) => (
    <button className="view-btn" onClick={() => setSelectedOrder(row)}>
      <FontAwesomeIcon icon={faEye} /> View
    </button>
  );

  const orderColumns = [
    { key: "orderNumber", label: "Order #", sortable: true },
    { key: "customerName", label: "Customer", sortable: true },
    {
      key: "total",
      label: "Amount",
      sortable: true,
      render: (row) => formatAmount(row.total?.usd || 0, true),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => renderOrderStatusBadge(row.status),
    },
    {
      key: "createdAt",
      label: "Date",
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
    { key: "actions", label: "Action", render: renderTableActions },
  ];

  // ============================================
  // MAIN RENDER
  // ============================================
  if (loading) return <Loader />;

  const dashboardTabs = getDashboardTabs();
  const displayAvatar = userAvatar;

  // Helper to check if a tab should be shown
  const shouldShowTab = (tabId) => {
    // If user has admin access, show all admin tabs
    if (hasAdminAccess) return true;

    // Team roles with specific permissions
    const role = getEffectiveRole();

    // Sales Representative
    if (role === "sale_representative") {
      return [
        "overview",
        "orders",
        "products",
        "preorders",
        "shipments",
        "profile",
      ].includes(tabId);
    }

    // Advisor
    if (role === "advisor") {
      return [
        "overview",
        "orders",
        "products",
        "preorders",
        "analytics",
        "testimonials",
        "profile",
      ].includes(tabId);
    }

    // Coordinator
    if (role === "coordinator") {
      return [
        "overview",
        "orders-all",
        "preorders",
        "shipments",
        "products",
        "analytics",
        "profile",
      ].includes(tabId);
    }

    // Manager
    if (role === "manager") {
      return [
        "overview",
        "orders-all",
        "preorders",
        "shipments",
        "products",
        "team",
        "analytics",
        "payments",
        "profile",
      ].includes(tabId);
    }

    // Administrator
    if (role === "administrator") {
      return [
        "overview",
        "orders-all",
        "preorders",
        "shipments",
        "products",
        "users",
        "team",
        "analytics",
        "payments",
        "profile",
      ].includes(tabId);
    }

    // Developer
    if (role === "developer") {
      return ["overview", "analytics", "ai-analytics", "profile"].includes(
        tabId,
      );
    }

    // Community Relation
    if (role === "community_relation") {
      return ["overview", "testimonials", "profile"].includes(tabId);
    }

    // Assistant roles
    if (role === "assistant_advisor" || role === "assistant_coordinator") {
      return [
        "overview",
        "orders",
        "products",
        "preorders",
        "profile",
      ].includes(tabId);
    }

    if (role === "assistant_manager") {
      return [
        "overview",
        "orders-all",
        "preorders",
        "shipments",
        "products",
        "analytics",
        "profile",
      ].includes(tabId);
    }

    // Farmer
    if (role === "farmer") {
      return [
        "overview",
        "orders",
        "preorders",
        "payments",
        "testimonials",
        "profile",
      ].includes(tabId);
    }

    // Supplier
    if (role === "supplier") {
      return [
        "overview",
        "supplier-products",
        "supplier-orders",
        "supplier-shipments",
        "payments",
        "profile",
      ].includes(tabId);
    }

    // Customer default
    return [
      "overview",
      "orders",
      "payments",
      "testimonials",
      "profile",
    ].includes(tabId);
  };

  // Filter tabs based on role
  const filteredTabs = dashboardTabs.filter((tab) => shouldShowTab(tab.id));

  // ============================================
  // RENDER STATS BASED ON ROLE
  // ============================================
  const renderStats = () => {
    // Super Admin
    if (isSuperAdminUser) {
      return (
        <>
          <StatCard
            icon={faBox}
            title="Total Products"
            value={stats.totalProducts || 0}
          />
          <StatCard
            icon={faShoppingCart}
            title="Total Orders"
            value={stats.totalOrders || 0}
          />
          <StatCard
            icon={faUsers}
            title="Total Users"
            value={stats.totalUsers || 0}
          />
          <StatCard
            icon={faTruck}
            title="Active Shipments"
            value={stats.activeShipments || 0}
          />
          <StatCard
            icon={faChartLine}
            title="Total Revenue"
            value={formatAmount(stats.totalRevenue || 0, true)}
          />
          <StatCard
            icon={faCalendarCheck}
            title="Pre-Orders"
            value={stats.totalPreOrders || 0}
          />
          <StatCard
            icon={faUserTie}
            title="Team Members"
            value={stats.teamMembers?.total || 0}
            sub={`${stats.teamMembers?.pending || 0} pending`}
          />
          <StatCard
            icon={faGlobe}
            title="Globally Sourced"
            value="100%"
            sub="Premium Quality Products"
            iconColor="#006B2E"
            borderColor="#006B2E"
          />
        </>
      );
    }

    // Admin
    if (isAdminUser && !isSuperAdminUser) {
      return (
        <>
          <StatCard
            icon={faBox}
            title="Total Products"
            value={stats.totalProducts || 0}
          />
          <StatCard
            icon={faShoppingCart}
            title="Total Orders"
            value={stats.totalOrders || 0}
          />
          <StatCard
            icon={faUsers}
            title="Total Users"
            value={stats.totalUsers || 0}
          />
          <StatCard
            icon={faTruck}
            title="Active Shipments"
            value={stats.activeShipments || 0}
          />
          <StatCard
            icon={faChartLine}
            title="Total Revenue"
            value={formatAmount(stats.totalRevenue || 0, true)}
          />
          <StatCard
            icon={faCalendarCheck}
            title="Pre-Orders"
            value={stats.totalPreOrders || 0}
          />
          <StatCard
            icon={faGlobe}
            title="Globally Sourced"
            value="100%"
            sub="Premium Quality Products"
            iconColor="#006B2E"
            borderColor="#006B2E"
          />
        </>
      );
    }

    // Team Member
    if (isTeamMemberUser && !hasAdminAccess) {
      return (
        <>
          <StatCard
            icon={faShoppingCart}
            title="My Orders"
            value={myStats.totalOrders || 0}
          />
          <StatCard
            icon={faBox}
            title="Total Items"
            value={myStats.totalItems || 0}
          />
          <StatCard
            icon={faDollarSign}
            title="Total Spent"
            value={formatAmount(myStats.totalSpent || 0, true)}
          />
          {canManagePreOrders && (
            <StatCard
              icon={faCalendarCheck}
              title="Pre-Orders"
              value={myStats.preOrders?.total || 0}
            />
          )}
          <StatCard
            icon={faUserTie}
            title="Team Role"
            value={getEffectiveRoleLabel()}
            sub={getEffectiveRole()}
            iconColor="#8b5cf6"
            borderColor="#8b5cf6"
          />
          <StatCard
            icon={faGlobe}
            title="Sourcing"
            value="Global"
            sub="Premium Quality Products"
            iconColor="#006B2E"
            borderColor="#006B2E"
          />
        </>
      );
    }

    // Farmer
    if (isFarmerUser) {
      return (
        <>
          <StatCard
            icon={faShoppingCart}
            title="My Orders"
            value={myStats.totalOrders || 0}
          />
          <StatCard
            icon={faBox}
            title="Total Items"
            value={myStats.totalItems || 0}
          />
          <StatCard
            icon={faDollarSign}
            title="Total Spent"
            value={formatAmount(myStats.totalSpent || 0, true)}
          />
          <StatCard
            icon={faTractor}
            title="Bulk Orders"
            value={myStats.bulkOrders || 0}
            iconColor="#D9A321"
            borderColor="#D9A321"
          />
          <StatCard
            icon={faCalendarCheck}
            title="My Pre-Orders"
            value={myStats.preOrders?.total || 0}
            sub={`${myStats.preOrders?.totalKg || 0} kg total`}
            iconColor="#8b5cf6"
            borderColor="#8b5cf6"
          />
          <StatCard
            icon={faGlobe}
            title="Sourcing"
            value="Global"
            sub="Premium Quality Products"
            iconColor="#006B2E"
            borderColor="#006B2E"
          />
        </>
      );
    }

    // Supplier
    if (isSupplierUser) {
      return (
        <>
          <StatCard
            icon={faBox}
            title="My Products"
            value={myStats.totalProducts || 0}
          />
          <StatCard
            icon={faShoppingCart}
            title="My Orders"
            value={myStats.totalOrders || 0}
          />
          <StatCard
            icon={faDollarSign}
            title="Total Revenue"
            value={formatAmount(myStats.totalRevenue || 0, true)}
          />
          <StatCard
            icon={faTruck}
            title="Shipments"
            value={myStats.activeShipments || 0}
          />
          <StatCard
            icon={faGlobe}
            title="Global Sourcing"
            value="Premium Quality"
            sub="Trusted Global Supply Chain"
            iconColor="#006B2E"
            borderColor="#006B2E"
          />
        </>
      );
    }

    // Customer (default)
    return (
      <>
        <StatCard
          icon={faShoppingCart}
          title="My Orders"
          value={myStats.totalOrders || 0}
        />
        <StatCard
          icon={faBox}
          title="Total Items"
          value={myStats.totalItems || 0}
        />
        <StatCard
          icon={faDollarSign}
          title="Total Spent"
          value={formatAmount(myStats.totalSpent || 0, true)}
        />
        <StatCard
          icon={faGlobe}
          title="Sourced"
          value="Global"
          sub="Premium Quality Products"
          iconColor="#006B2E"
          borderColor="#006B2E"
        />
      </>
    );
  };

  return (
    <div className="dashboard-container">
      {/* ========== NOTIFICATION ========== */}
      {notification.message && (
        <div className={`notification ${notification.type}`}>
          <FontAwesomeIcon
            icon={
              notification.type === "success"
                ? faCheckCircle
                : faExclamationTriangle
            }
          />
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ type: "", message: "" })}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* ========== HEADER ========== */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-user-info">
            <div className="header-avatar">
              {displayAvatar ? (
                <img
                  src={displayAvatar}
                  alt={user?.name || "User"}
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <span className="avatar-initials">
                  {user?.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2) || "U"}
                </span>
              )}
            </div>
            <div className="header-text">
              <h1>
                <FontAwesomeIcon
                  icon={getRoleHeaderIcon()}
                  className="role-icon"
                />
                {getDashboardTitle()}
              </h1>
              <p>Welcome back, {user?.name}</p>
              <div className="header-badges">
                <span className={`role-badge ${user?.role || "customer"}`}>
                  <FontAwesomeIcon icon={getRoleHeaderIcon()} />
                  {getEffectiveRoleLabel()}
                </span>
                <button
                  className="currency-toggle"
                  onClick={toggleCurrency}
                  title={`Switch to ${currency === "SLL" ? "USD" : "SLL"}`}
                >
                  <FontAwesomeIcon icon={faExchangeAlt} />
                  {currency}
                </button>
                <span className="sourcing-badge">
                  <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <a
            href="https://wa.me/23231030808"
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-support"
          >
            <FontAwesomeIcon icon={faWhatsappBrand} /> Support
          </a>
          <button onClick={logout} className="logout-btn">
            <FontAwesomeIcon icon={faTimes} /> Logout
          </button>
        </div>
      </div>

      {/* ========== STATS GRID ========== */}
      <StatsGrid stats={stats}>
        {renderStats()}
      </StatsGrid>

      {/* ========== LOW STOCK ALERT - Admin only ========== */}
      {lowStockProducts.length > 0 && canManageProducts && (
        <div className="alert-section">
          <h3>
            <FontAwesomeIcon icon={faExclamationTriangle} /> Low Stock Alert
          </h3>
          <div className="low-stock-grid">
            {lowStockProducts.map((product) => (
              <div key={product._id} className="low-stock-card">
                <span>
                  {product.name}
                  <span className="origin-tag">
                    <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                  </span>
                </span>
                <span className="stock-count">{product.stock} units left</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========== TABS ========== */}
      {filteredTabs.length > 0 && (
        <DashboardTabs
          tabs={filteredTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      )}

      {/* ========== TAB CONTENT ========== */}
      <div className="tab-content">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="recent-section">
            <h3>
              <FontAwesomeIcon icon={faClock} />{" "}
              {hasAdminAccess ? "Recent Orders" : "My Recent Orders"}
            </h3>
            <DataTable
              columns={orderColumns}
              data={hasAdminAccess ? recentOrders : myOrders.slice(0, 5)}
            />
          </div>
        )}

        {/* My Orders Tab */}
        {activeTab === "orders" && (
          <div className="recent-section">
            <h3>
              <FontAwesomeIcon icon={faShoppingCart} /> My Orders
            </h3>
            <DataTable
              columns={[
                { key: "orderNumber", label: "Order #" },
                {
                  key: "createdAt",
                  label: "Date",
                  render: (row) => new Date(row.createdAt).toLocaleDateString(),
                },
                {
                  key: "items",
                  label: "Items",
                  render: (row) => `${row.items?.length || 0} items`,
                },
                {
                  key: "totalQuantity",
                  label: "Quantity",
                  render: (row) => `${row.totalQuantity || 0} kg`,
                },
                {
                  key: "total",
                  label: "Total",
                  render: (row) => formatAmount(row.total?.usd || 0, true),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (row) => renderOrderStatusBadge(row.status),
                },
                {
                  key: "actions",
                  label: "Action",
                  render: (row) => (
                    <button
                      className="view-btn"
                      onClick={() => setSelectedOrder(row)}
                    >
                      <FontAwesomeIcon icon={faEye} /> Details
                    </button>
                  ),
                },
              ]}
              data={myOrders}
              rowClassName={(row) =>
                row.orderType === "bulk_farmer" ? "bulk-order-row" : ""
              }
            />
          </div>
        )}

        {/* All Orders Tab - Admin/Team with permission */}
        {activeTab === "orders-all" && (hasAdminAccess || canManageOrders) && (
          <div className="tab-content">
            <div className="section-header">
              <h2>
                <FontAwesomeIcon icon={faShoppingCart} /> All Orders
              </h2>
              <div className="filters">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="shipped">Shipped</option>
                  <option value="out_for_delivery">Out for Delivery</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <DataTable
              columns={[
                {
                  key: "orderNumber",
                  label: "Order #",
                  render: (row) => (
                    <React.Fragment>
                      <strong>{row.orderNumber}</strong>
                      {row.orderType === "bulk_farmer" && (
                        <span className="bulk-badge">BULK</span>
                      )}
                    </React.Fragment>
                  ),
                },
                { key: "customerName", label: "Customer" },
                { key: "customerEmail", label: "Email" },
                {
                  key: "items",
                  label: "Items",
                  render: (row) => `${row.items?.length || 0} items`,
                },
                {
                  key: "total",
                  label: "Amount",
                  render: (row) => formatAmount(row.total?.usd || 0, true),
                },
                {
                  key: "orderType",
                  label: "Type",
                  render: (row) => (
                    <span
                      className={`order-type-badge ${row.orderType || "retail"}`}
                    >
                      {row.orderType === "bulk_farmer" ? "Bulk" : "Retail"}
                    </span>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (row) => (
                    <select
                      value={row.status}
                      onChange={(e) =>
                        handleUpdateOrderStatus(row._id, e.target.value)
                      }
                      className="status-select"
                      style={{ borderColor: getStatusColor(row.status) }}
                    >
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="shipped">Shipped</option>
                      <option value="out_for_delivery">Out for Delivery</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  ),
                },
                {
                  key: "createdAt",
                  label: "Date",
                  render: (row) => new Date(row.createdAt).toLocaleDateString(),
                },
                {
                  key: "actions",
                  label: "Actions",
                  render: (row) => (
                    <button
                      className="view-btn"
                      onClick={() => setSelectedOrder(row)}
                    >
                      <FontAwesomeIcon icon={faEye} /> Details
                    </button>
                  ),
                },
              ]}
              data={allOrders}
              rowClassName={(row) =>
                row.orderType === "bulk_farmer" ? "bulk-order-row" : ""
              }
            />
          </div>
        )}

        {/* Pre-Orders Tab */}
        {activeTab === "preorders" &&
          (hasAdminAccess || canManagePreOrders) && (
            <PreOrderManager
              preOrders={preOrders}
              stats={preOrderStats}
              onUpdateStatus={handleUpdatePreOrderStatus}
              onRefresh={fetchAllData}
              availableSizes={availableSizes}
              preOrderSizes={preOrderSizes}
            />
          )}

        {/* Shipments Tab */}
        {activeTab === "shipments" &&
          (hasAdminAccess || canManageShipments) && (
            <div className="tab-content">
              <div className="section-header">
                <h2>
                  <FontAwesomeIcon icon={faTruck} /> Shipment Tracking
                </h2>
                <div className="filters">
                  <span className="shipment-stats">
                    <FontAwesomeIcon icon={faGlobe} /> Globally Sourced:{" "}
                    {shipmentStats.fromGlobal || 0}
                    <span className="stat-sep">|</span>
                    <FontAwesomeIcon icon={faBoxes} /> Bulk:{" "}
                    {shipmentStats.bulk || 0}
                  </span>
                  {canManageShipments && (
                    <button
                      className="btn-primary"
                      onClick={() => setShowShipmentForm(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Create Shipment
                    </button>
                  )}
                </div>
              </div>
              <DataTable
                columns={[
                  {
                    key: "trackingNumber",
                    label: "Tracking #",
                    render: (row) => (
                      <React.Fragment>
                        <strong>{row.trackingNumber}</strong>
                        <span className="origin-badge">
                          <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                        </span>
                      </React.Fragment>
                    ),
                  },
                  {
                    key: "order",
                    label: "Order #",
                    render: (row) => row.order?.orderNumber || "Inventory",
                  },
                  {
                    key: "orderType",
                    label: "Type",
                    render: (row) => (
                      <span
                        className={`shipment-type-badge ${row.orderType || "retail"}`}
                      >
                        {row.orderType === "bulk_farmer"
                          ? "Bulk"
                          : row.orderType === "inventory"
                            ? "Inventory"
                            : "Standard"}
                      </span>
                    ),
                  },
                  {
                    key: "origin",
                    label: "Origin",
                    render: (row) => (
                      <React.Fragment>
                        <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                      </React.Fragment>
                    ),
                  },
                  {
                    key: "destination",
                    label: "Destination",
                    render: (row) =>
                      row.destination?.city || "Freetown, Sierra Leone",
                  },
                  {
                    key: "status",
                    label: "Status",
                    render: (row) => renderOrderStatusBadge(row.status),
                  },
                  {
                    key: "estimatedDelivery",
                    label: "Est. Delivery",
                    render: (row) =>
                      row.estimatedDelivery
                        ? new Date(row.estimatedDelivery).toLocaleDateString()
                        : "-",
                  },
                  {
                    key: "actions",
                    label: "Actions",
                    render: (row) => (
                      <button
                        className="view-btn"
                        onClick={() => setSelectedShipment(row)}
                      >
                        <FontAwesomeIcon icon={faEdit} /> Update
                      </button>
                    ),
                  },
                ]}
                data={allShipments}
                rowClassName={(row) =>
                  row.orderType === "bulk_farmer" ? "bulk-shipment-row" : ""
                }
              />
            </div>
          )}

        {/* Products Tab - Admin/Team with permission */}
        {activeTab === "products" && (hasAdminAccess || canManageProducts) && (
          <div className="tab-content">
            <div className="section-header">
              <h2>
                <FontAwesomeIcon icon={faBox} /> Product Management
              </h2>
              <div className="filters">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="size-indicator">
                  <FontAwesomeIcon icon={faWeightScale} /> Sizes:{" "}
                  {availableSizes.length > 0
                    ? availableSizes.join("kg, ") + "kg"
                    : "2kg, 10kg, 25kg, 50kg"}
                  {preOrderSizes.length > 0 && (
                    <span className="preorder-sizes">
                      {" "}
                      | Pre-order: {preOrderSizes.join("kg, ")}kg
                    </span>
                  )}
                </span>
                <span className="sourcing-indicator">
                  <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                </span>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setEditingProduct(null);
                    setShowProductForm(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Product
                </button>
              </div>
            </div>
            <DataTable
              columns={[
                {
                  key: "image",
                  label: "Image",
                  render: (row) => (
                    <img
                      src={getProductImage(row)}
                      alt={row.name}
                      className="product-thumb"
                    />
                  ),
                },
                { key: "name", label: "Name", sortable: true },
                {
                  key: "category",
                  label: "Category",
                  sortable: true,
                  render: (row) => row.category?.replace(/-/g, " "),
                },
                {
                  key: "origin",
                  label: "Source",
                  sortable: true,
                  render: (row) => (
                    <span className="origin-tag">
                      <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                    </span>
                  ),
                },
                {
                  key: "basePrice",
                  label: "Price",
                  sortable: true,
                  render: (row) =>
                    `${formatAmount(row.basePrice?.usd || 0, true)}/${row.unit || "kg"}`,
                },
                {
                  key: "stock",
                  label: "Stock",
                  sortable: true,
                  render: (row) => (
                    <span
                      className={
                        row.stock < (row.lowStockThreshold || 100)
                          ? "low-stock"
                          : ""
                      }
                    >
                      {row.stock}
                    </span>
                  ),
                },
                {
                  key: "sizes",
                  label: "Sizes",
                  render: (row) => (
                    <span className="size-tags">
                      {row.availableSizes?.length > 0 ? (
                        row.availableSizes.map((size) => (
                          <span key={size} className="size-tag">
                            {size}kg
                            {row.preOrderSizes?.includes(size) && (
                              <span className="preorder-tag">
                                <FontAwesomeIcon icon={faCalendarCheck} />
                              </span>
                            )}
                          </span>
                        ))
                      ) : (
                        <span className="size-tag">2, 10, 25, 50kg</span>
                      )}
                    </span>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (row) => (
                    <span
                      className={`status-badge ${row.isAvailable ? "active" : "inactive"}`}
                    >
                      {row.isAvailable ? "Active" : "Inactive"}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  label: "Actions",
                  render: (row) => (
                    <div className="action-buttons">
                      <button
                        className="edit-btn"
                        onClick={() => {
                          setEditingProduct(row);
                          setShowProductForm(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteProduct(row._id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ),
                },
              ]}
              data={allProducts}
            />
          </div>
        )}

        {/* Users Tab - Admin only */}
        {activeTab === "users" && hasAdminAccess && canManageUsers && (
          <div className="tab-content">
            <div className="section-header">
              <h2>
                <FontAwesomeIcon icon={faUsers} /> User Management
              </h2>
              <div className="header-actions">
                {canManageTeam && (
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setEditingTeamMember(null);
                      setShowTeamMemberForm(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faUserPlus} /> Add Team Member
                  </button>
                )}
              </div>
            </div>
            <DataTable
              columns={[
                {
                  key: "name",
                  label: "Name",
                  render: (row) => (
                    <React.Fragment>
                      <strong>{row.name}</strong>
                      {row._id === user?._id && (
                        <span className="current-user-badge">You</span>
                      )}
                      {row.isTeamMember && (
                        <span className="team-member-badge">
                          {row.isApproved ? "Team" : "Pending"}
                        </span>
                      )}
                    </React.Fragment>
                  ),
                },
                { key: "email", label: "Email" },
                {
                  key: "role",
                  label: "Role",
                  render: (row) => (
                    <span className={`role-badge ${row.role}`}>
                      {row.role === "super_admin" && (
                        <FontAwesomeIcon icon={faCrown} />
                      )}
                      {row.role === "admin" && (
                        <FontAwesomeIcon icon={faShieldAlt} />
                      )}
                      {row.isTeamMember && row.teamRole && (
                        <FontAwesomeIcon icon={faUserTie} />
                      )}
                      {row.role === "super_admin"
                        ? "Super Admin"
                        : row.role === "admin"
                          ? "Admin"
                          : row.isTeamMember && row.teamRole
                            ? row.teamRoleLabel || row.teamRole
                            : row.role === "farmer"
                              ? "Farmer"
                              : row.role === "supplier"
                                ? "Supplier"
                                : "Customer"}
                    </span>
                  ),
                },
                {
                  key: "userType",
                  label: "Type",
                  render: (row) =>
                    row.userType === "farmer" ? (
                      <span className="farmer-badge">
                        <FontAwesomeIcon icon={faTractor} />{" "}
                        {row.farmDetails?.farmSize || "small"} farm
                      </span>
                    ) : row.userType === "supplier" ? (
                      <span className="supplier-badge">
                        <FontAwesomeIcon icon={faBuilding} /> Supplier
                      </span>
                    ) : row.isTeamMember ? (
                      <span className="team-badge">
                        <FontAwesomeIcon icon={faUserTie} /> Team
                      </span>
                    ) : (
                      <span className="customer-badge">
                        <FontAwesomeIcon icon={faStore} /> Retail
                      </span>
                    ),
                },
                {
                  key: "phone",
                  label: "Phone",
                  render: (row) => row.phone || "-",
                },
                {
                  key: "createdAt",
                  label: "Joined",
                  render: (row) => new Date(row.createdAt).toLocaleDateString(),
                },
                {
                  key: "isActive",
                  label: "Status",
                  render: (row) => (
                    <button
                      className={`status-toggle ${row.isActive ? "active" : "inactive"}`}
                      onClick={() => handleToggleUserStatus(row._id)}
                      disabled={row._id === user?._id || !canManageUsers}
                    >
                      <FontAwesomeIcon
                        icon={row.isActive ? faToggleOn : faToggleOff}
                      />
                      {row.isActive ? "Active" : "Inactive"}
                    </button>
                  ),
                },
                {
                  key: "actions",
                  label: "Actions",
                  render: (row) => (
                    <div className="action-buttons">
                      {canManageTeam && (
                        <button
                          className="edit-btn"
                          onClick={() => {
                            setEditingTeamMember(row);
                            setShowTeamMemberForm(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      )}
                    </div>
                  ),
                },
              ]}
              data={users}
              rowClassName={(row) => (!row.isActive ? "inactive-row" : "")}
            />
          </div>
        )}

        {/* Team Management Tab */}
        {activeTab === "team" && canManageTeam && (
          <TeamManagement
            isAdmin={isAdminUser}
            isSuperAdmin={isSuperAdminUser}
            canApprove={canApproveTeam}
            canReject={hasPermission("canRejectTeam")}
          />
        )}

        {/* Supplier Tabs */}
        {activeTab === "supplier-products" && isSupplierUser && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>
                <FontAwesomeIcon icon={faBox} /> My Products
              </h2>
              <button className="btn-primary" onClick={() => {}}>
                <FontAwesomeIcon icon={faPlus} /> Add Product
              </button>
            </div>
            <div className="supplier-products">
              <p className="no-data">
                <FontAwesomeIcon icon={faGlobe} /> Globally Sourced Products
                {user?.supplier
                  ? ` - ${user.supplier.companyName}`
                  : " - Manage your products"}
              </p>
            </div>
          </div>
        )}

        {activeTab === "supplier-orders" && isSupplierUser && (
          <div className="dashboard-section">
            <h2>
              <FontAwesomeIcon icon={faShoppingCart} /> Supplier Orders
            </h2>
            <div className="supplier-orders">
              <p className="no-data">
                <FontAwesomeIcon icon={faGlobe} /> Globally Sourced Orders
              </p>
            </div>
          </div>
        )}

        {activeTab === "supplier-shipments" && isSupplierUser && (
          <div className="dashboard-section">
            <h2>
              <FontAwesomeIcon icon={faTruck} /> Supplier Shipments
            </h2>
            <div className="supplier-shipments">
              <p className="no-data">
                <FontAwesomeIcon icon={faGlobe} /> Globally Sourced Shipments
              </p>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (canManageAnalytics || hasAdminAccess) && (
          <div className="tab-content analytics-tab">
            <AnalyticsCards
              analytics={analytics}
              userRole={user?.role}
              userType={user?.userType}
            />
            <div className="analytics-grid">
              <SalesChart data={analytics.salesData || []} />
              <TrafficSources
                sources={analytics.trafficSources || []}
                userRole={user?.role}
              />
            </div>
            <TopProducts products={analytics.topProducts || []} />
          </div>
        )}

        {/* AI Analytics Tab */}
        {activeTab === "ai-analytics" &&
          (isAdminUser || getEffectiveRole() === "developer") && (
            <div className="ai-analytics-tab">
              <AIStatsCard />
            </div>
          )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>
                <FontAwesomeIcon icon={faCreditCard} /> Payment Management
              </h2>
            </div>
            <PaymentMethods
              paymentMethods={paymentMethods}
              onAddPaymentMethod={handleAddPaymentMethod}
              onRemovePaymentMethod={handleRemovePaymentMethod}
              setNewPaymentMethod={setNewPaymentMethod}
              newPaymentMethod={newPaymentMethod}
              showAddPaymentModal={showAddPaymentModal}
              setShowAddPaymentModal={setShowAddPaymentModal}
              onRefresh={fetchPaymentData}
            />
            {(isFarmerUser || isSupplierUser || isTeamMemberUser) && (
              <>
                <div className="payout-divider" />
                <PayoutComponent
                  payoutHistory={payoutHistory}
                  payoutRequest={payoutRequest}
                  setPayoutRequest={setPayoutRequest}
                  showPayoutModal={showPayoutModal}
                  setShowPayoutModal={setShowPayoutModal}
                  onRequestPayout={handleRequestPayout}
                  myStats={myStats}
                />
              </>
            )}
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === "testimonials" && (
          <TestimonialManager isAdmin={canManageTestimonials || isAdminUser} />
        )}

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="dashboard-section">
            <h2>
              <FontAwesomeIcon icon={faUserCircle} /> Profile Settings
            </h2>
            {profileMessage.text && (
              <div className={`alert-message ${profileMessage.type}`}>
                <FontAwesomeIcon
                  icon={
                    profileMessage.type === "success"
                      ? faCheckCircle
                      : faExclamationTriangle
                  }
                />
                {profileMessage.text}
              </div>
            )}
            <form onSubmit={handleProfileUpdate} className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faUserCircle} /> Full Name
                  </label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) =>
                      setProfileData({ ...profileData, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faEnvelope} /> Email
                  </label>
                  <input type="email" value={profileData.email} disabled />
                </div>
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faPhone} /> Phone Number
                </label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) =>
                    setProfileData({ ...profileData, phone: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faMapMarkerAlt} /> Street Address
                </label>
                <input
                  type="text"
                  value={profileData.address?.street || ""}
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      address: {
                        ...profileData.address,
                        street: e.target.value,
                      },
                    })
                  }
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={profileData.address?.city || ""}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        address: {
                          ...profileData.address,
                          city: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>District</label>
                  <input
                    type="text"
                    value={profileData.address?.district || ""}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        address: {
                          ...profileData.address,
                          district: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    value={profileData.address?.country || "Sierra Leone"}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        address: {
                          ...profileData.address,
                          country: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>
              <button type="submit" className="btn-primary" disabled={updating}>
                <FontAwesomeIcon icon={faSave} />{" "}
                {updating ? "Saving..." : "Save Changes"}
              </button>
            </form>

            <div className="password-section">
              <h3>
                <FontAwesomeIcon icon={faKey} /> Change Password
              </h3>
              <form onSubmit={handlePasswordChange} className="password-form">
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          newPassword: e.target.value,
                        })
                      }
                      required
                      minLength="6"
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          confirmPassword: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn-secondary"
                  disabled={updating}
                >
                  <FontAwesomeIcon icon={faKey} /> Change Password
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* ========== MODALS ========== */}
      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdateStatus={handleUpdateOrderStatus}
          isAdmin={canManageOrders || hasAdminAccess}
        />
      )}
      {selectedShipment && (
        <ShipmentDetailsModal
          shipment={selectedShipment}
          onClose={() => setSelectedShipment(null)}
          onUpdate={handleUpdateShipmentStatus}
          updateData={shipmentUpdateData}
          setUpdateData={setShipmentUpdateData}
          isAdmin={canManageShipments || hasAdminAccess}
        />
      )}
      {showProductForm && (
        <ProductForm
          product={editingProduct}
          onClose={() => {
            setShowProductForm(false);
            setEditingProduct(null);
          }}
          onSave={() => {
            setShowProductForm(false);
            setEditingProduct(null);
            fetchAllData();
          }}
        />
      )}
      {showShipmentForm && (
        <ShipmentForm
          orders={allOrders}
          onClose={() => setShowShipmentForm(false)}
          onSave={() => {
            setShowShipmentForm(false);
            fetchAllData();
          }}
        />
      )}
      {showTeamMemberForm && (
        <TeamMemberForm
          member={editingTeamMember}
          onClose={() => {
            setShowTeamMemberForm(false);
            setEditingTeamMember(null);
          }}
          onSave={() => {
            setShowTeamMemberForm(false);
            setEditingTeamMember(null);
            fetchAllData();
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;